import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, FileStack, Boxes, Users, Wallet, AlertTriangle,
  LogOut, Building2, Scale, Landmark, BarChart3, ClipboardCheck, BookOpen,
  PanelLeftClose, PanelLeftOpen, Receipt, CalendarClock, IndianRupee, LayoutGrid, ListChecks, DatabaseZap, Archive,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { NAV, AUTH } from '@/constants/testIds';
import { R } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { NotificationBell } from '@/components/NotificationBell';

const NAV_ITEMS = [
  { to: R.overview, label: 'Dashboard', icon: LayoutDashboard, testId: NAV.dashboard },
  { to: R.reminders, label: 'Reminders & Tasks', icon: ListChecks, testId: 'nav-reminders' },
  { to: R.eoiPunch, label: 'New EOI', icon: FileStack, testId: NAV.eoiPunch, requires: 'can_create_eoi' },
  { to: R.eoiHub, label: 'EOI Hub', icon: ClipboardCheck, testId: NAV.eoiHub },
  { to: R.bookings, label: 'Active Bookings', icon: BookOpen, testId: NAV.bookings, end: true },
  { to: R.legacyArchive, label: 'Legacy Archive', icon: Archive, testId: 'nav-legacy-archive' },
  { to: R.inventory, label: 'Projects & Inventory', icon: Boxes, testId: NAV.inventory },
  { to: R.customers, label: 'Customers', icon: Users, testId: NAV.customers },
  { to: R.legal, label: 'Legal Portal', icon: Scale, testId: NAV.legal },
  { to: R.legalCalendar, label: 'Legal Calendar', icon: CalendarClock, testId: NAV.legalCalendar },
  { to: R.loan, label: 'Loan Tracker', icon: Landmark, testId: NAV.loan },
  { to: R.pricingTracker, label: 'Rate Tracker', icon: IndianRupee, testId: NAV.pricingTracker, adminOnly: true },
  { to: R.accounts, label: 'Accounts & Payments', icon: Wallet, testId: NAV.accounts, end: true },
  { to: R.recentPayments, label: 'Recent Collections', icon: Receipt, testId: NAV.recentPayments },
  { to: R.recovery, label: 'Recovery & CRM', icon: AlertTriangle, testId: NAV.recovery },
  { to: R.reports, label: 'Reports & Analytics', icon: BarChart3, testId: NAV.reports, requires: 'can_view_reports' },
  { to: R.legacyMigration, label: 'Legacy Migration', icon: DatabaseZap, testId: 'nav-legacy-migration', adminOnly: true },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('kesar_sidebar_collapsed') === '1');

  const toggle = () => setCollapsed((c) => {
    const next = !c;
    localStorage.setItem('kesar_sidebar_collapsed', next ? '1' : '0');
    return next;
  });

  const items = NAV_ITEMS.filter((i) => {
    if (i.adminOnly) return user?.permissions?.is_admin;
    if (i.requires) return user?.permissions?.[i.requires];
    return true;
  });

  return (
    <div className="flex h-screen w-full overflow-hidden bg-[#FAF8F5]">
      <aside
        data-testid="app-sidebar"
        data-collapsed={collapsed ? 'true' : 'false'}
        className={cn(
          'flex h-screen flex-col border-r border-[#E8E3DB] bg-[#FFFDF9] transition-[width] duration-200 ease-in-out',
          collapsed ? 'w-16' : 'w-60'
        )}
      >
        {/* Brand */}
        <div className={cn('flex h-16 items-center gap-3 border-b border-[#E8E3DB] px-3', collapsed && 'justify-center px-0')}>
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-[#E8E3DB]">
            <img src="/favicon.png" alt="Kesar" className="h-7 w-7 object-contain" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-head text-lg font-extrabold leading-tight tracking-tight text-[#1E1C1A]">KesarOne</div>
              <div className="truncate text-[10px] font-semibold uppercase tracking-[0.15em] text-[#736E67]">Kesar Lands CRM</div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-3">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={!!item.end}
              data-testid={item.testId}
              title={collapsed ? item.label : undefined}
              aria-label={item.label}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150',
                  collapsed && 'justify-center px-0',
                  isActive
                    ? 'border-r-2 border-[#245329] bg-[#E6F4EA] text-[#245329]'
                    : 'text-[#736E67] hover:bg-[#F0EDE5] hover:text-[#1E1C1A]'
                )
              }
            >
              <item.icon className="h-5 w-5 shrink-0" strokeWidth={2} />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Collapse toggle */}
        <button
          onClick={toggle}
          data-testid="sidebar-toggle"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={cn(
            'flex items-center gap-2 border-t border-[#E8E3DB] px-4 py-2.5 text-xs font-semibold text-[#736E67] transition-colors hover:bg-[#F0EDE5] hover:text-[#1E1C1A]',
            collapsed && 'justify-center px-0'
          )}
        >
          {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : (<><PanelLeftClose className="h-4 w-4" /><span>Collapse</span></>)}
        </button>

        {/* Switch Module — only for users mapped to more than one ecosystem module */}
        {(user?.modules?.length || 0) > 1 && (
          <button
            onClick={() => navigate(R.hub)}
            data-testid="switch-module-btn"
            title={collapsed ? 'Switch module' : undefined}
            aria-label="Switch module"
            className={cn(
              'flex items-center gap-3 border-t border-[#E8E3DB] px-4 py-2.5 text-sm font-semibold text-[#245329] transition-colors hover:bg-[#E6F4EA]',
              collapsed && 'justify-center px-0'
            )}
          >
            <LayoutGrid className="h-5 w-5 shrink-0" strokeWidth={2} />
            {!collapsed && <span>Switch Module</span>}
          </button>
        )}

        {/* User */}
        <div className="border-t border-[#E8E3DB] p-2">
          <div className={cn('flex items-center gap-2 rounded-md p-2', collapsed && 'justify-center p-1')}>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#E8E3DB] text-sm font-bold text-[#736E67]">
              {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name?.[0] || '?')}
            </div>
            {!collapsed && (
              <>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold text-[#1E1C1A]">{user?.name}</div>
                  <div className="truncate text-xs font-medium text-[#736E67]">{user?.department} · {user?.role}</div>
                </div>
                <button
                  data-testid={AUTH.logoutBtn}
                  onClick={logout}
                  className="rounded-md p-1.5 text-[#736E67] transition-colors hover:bg-[#FCE8E8] hover:text-[#8B1E1E]"
                  title="Sign out"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
          {collapsed && (
            <button
              data-testid={AUTH.logoutBtn}
              onClick={logout}
              className="mt-1 flex w-full justify-center rounded-md p-1.5 text-[#736E67] transition-colors hover:bg-[#FCE8E8] hover:text-[#8B1E1E]"
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          )}
        </div>
      </aside>

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-16 shrink-0 items-center gap-4 border-b border-[#E8E3DB] bg-[#FFFDF9] px-6">
          <span className="font-head text-base font-bold tracking-tight text-[#1E1C1A]">Kesar Lands · Internal CRM</span>
          <div className="ml-auto flex items-center gap-2">
            <NotificationBell />
          </div>
        </header>
        <main className="flex-1 overflow-auto px-6 py-6">{children}</main>
      </div>
    </div>
  );
};
